/** EGB-00004 — Enterprise Security Governance Framework (mirrors pkg/securitygovernance) */

export const EGB_SEC_ID = 'EGB-00004';

export interface CouncilMemberRow {
  role: string;
  responsibility: string;
  decisionRights: string;
  reviewCycle: string;
}

export interface ZeroTrustRow {
  pillar: string;
  status: string;
  score: number;
}

export interface ControlRow {
  domain: string;
  control: string;
  status: string;
}

export interface PolicyRow {
  area: string;
  policy: string;
  status: string;
}

export interface InfraSecOpsRow {
  area: string;
  detail: string;
  status: string;
}

export interface IncidentVulnRow {
  category: string;
  item: string;
  status: string;
  count: number;
}

export interface ComplianceDashboardRow {
  metric: string;
  value: string;
  status: string;
  score: number;
}

export const COUNCIL_ROLES = [
  'CISO', 'Chief Enterprise Architect', 'Chief AI Officer', 'Product Security Lead',
  'Infrastructure Security Lead', 'Privacy Officer', 'Legal & Compliance Officer',
  'Marketplace Security Specialist', 'DevSecOps Lead', 'Accessibility Specialist',
];

export const COUNCIL_FUNCTIONS = ['Roles', 'Responsibilities', 'Decision Rights', 'Approval Matrix', 'Risk Acceptance', 'Review Cycles'];

export const ZERO_TRUST_PILLARS = [
  'Continuous Verification', 'Least Privilege', 'Identity Trust', 'Device Trust', 'Network Trust', 'Adaptive Access',
];

export const IDENTITY_ACCESS = [
  'Identity Lifecycle', 'RBAC', 'ABAC', 'MFA', 'SSO', 'Passwordless Authentication', 'Privileged Access', 'Delegated Access', 'Periodic Access Reviews',
];

export const SECRETS_GOVERNANCE = [
  'API Keys', 'Marketplace Credentials', 'LLM Credentials', 'Certificates', 'Encryption Keys', 'Vault Integration', 'Rotation Policies', 'Audit Trails',
];

export const DATA_SECURITY = [
  'Data Classification', 'Encryption at Rest', 'Encryption in Transit', 'Masking', 'Tokenization', 'Retention', 'Secure Deletion',
];

export const PRIVACY_GOVERNANCE = [
  'GDPR', 'DPDP', 'Consent Management', 'Data Subject Rights', 'Purpose Limitation', 'Data Minimization', 'Privacy Audits',
];

export const MARKETPLACE_SECURITY = [
  'Amazon SP-API', 'OAuth', 'Webhook Validation', 'Credential Rotation', 'Marketplace Compliance', 'Rate Limiting',
];

export const AI_SECURITY = [
  'Prompt Security', 'Model Security', 'Agent Isolation', 'Sensitive Data Detection', 'Prompt Injection Defense', 'Output Validation',
];

export const APPLICATION_SECURITY = [
  'OWASP Top 10', 'SAST', 'DAST', 'Dependency Scanning', 'API Security', 'Session Protection', 'Secrets Detection',
];

export const INFRASTRUCTURE_SECURITY = [
  'Servers', 'Containers', 'Databases', 'Redis', 'Networking', 'Firewalls', 'VPN', 'WAF', 'DDoS Protection',
];

export const SECOPS = [
  'Continuous Monitoring', 'Threat Detection', 'Threat Hunting', 'SIEM', 'Incident Detection', 'Correlation',
];

export const INCIDENT_RESPONSE = [
  'Preparation', 'Identification', 'Containment', 'Eradication', 'Recovery', 'Evidence Preservation', 'Post-Incident Review',
];

export const VULNERABILITY_MANAGEMENT = ['Scanning', 'Risk Prioritization', 'Patch Management', 'Verification', 'Reporting'];

export const COMPLIANCE_STANDARDS = ['SOC2 Readiness', 'ISO 27001 Readiness', 'GDPR', 'DPDP', 'Marketplace Compliance'];

export const BUSINESS_CONTINUITY = ['Risk Management', 'Backup Strategy', 'Disaster Recovery', 'Recovery Drills', 'Vendor Risk', 'Communication Plans'];

export const SECURITY_METRICS = ['Risk Scores', 'Threat Trends', 'Incident Metrics', 'Compliance Status', 'Patch Compliance', 'Access Review Results'];

export const SECURITY_AWARENESS = ['Developer Training', 'Employee Training', 'AI Security Training', 'Marketplace Security Education', 'Phishing Simulations'];

export const EXECUTIVE_DASHBOARD = ['Security Score', 'Compliance Score', 'Critical Risks', 'Incident Status', 'Audit Readiness', 'Business Risk'];

export const ARCHITECTURE_STAGES = [
  'Security Council', 'Zero Trust & Identity', 'Secrets & Data', 'Privacy & Marketplace',
  'AI & Application Security', 'Infrastructure & SecOps', 'Incident & Vulnerability', 'Compliance Dashboard',
];

export const LIVE_ROUTES: Record<string, string> = {
  masterConstitution: '/platform/master-constitution',
  aiGovernance: '/platform/ai-governance',
  governance: '/governance',
  enterpriseQuality: '/platform/enterprise-quality',
};

export const DEMO_COUNCIL: CouncilMemberRow[] = [
  { role: 'CISO', responsibility: 'Security strategy & risk acceptance', decisionRights: 'Final security approval', reviewCycle: 'Weekly' },
  { role: 'Privacy Officer', responsibility: 'GDPR, DPDP, consent management', decisionRights: 'Privacy sign-off', reviewCycle: 'Monthly' },
  { role: 'DevSecOps Lead', responsibility: 'CI/CD security gates & SAST/DAST', decisionRights: 'Pipeline security veto', reviewCycle: 'Weekly' },
  { role: 'Marketplace Security Specialist', responsibility: 'Amazon SP-API & OAuth security', decisionRights: 'Marketplace credential approval', reviewCycle: 'Bi-weekly' },
  { role: 'Product Security Lead', responsibility: 'OWASP & application security', decisionRights: 'Release security gate', reviewCycle: 'Weekly' },
  { role: 'Infrastructure Security Lead', responsibility: 'Infrastructure & Zero Trust', decisionRights: 'Network policy approval', reviewCycle: 'Weekly' },
];

export const DEMO_ZERO_TRUST: ZeroTrustRow[] = [
  { pillar: 'Continuous Verification', status: 'enforced', score: 98 },
  { pillar: 'Least Privilege', status: 'enforced', score: 97 },
  { pillar: 'Identity Trust', status: 'enforced', score: 99 },
  { pillar: 'Adaptive Access', status: 'active', score: 95 },
  { pillar: 'Network Trust', status: 'enforced', score: 96 },
];

export const DEMO_SECRETS_DATA: ControlRow[] = [
  { domain: 'Secrets', control: 'Vault integration with rotation', status: 'enforced' },
  { domain: 'Secrets', control: 'Marketplace credential isolation', status: 'enforced' },
  { domain: 'Data', control: 'Encryption at rest (AES-256)', status: 'enforced' },
  { domain: 'Data', control: 'Encryption in transit (TLS 1.3)', status: 'enforced' },
  { domain: 'Data', control: 'Data classification + masking', status: 'active' },
];

export const DEMO_PRIVACY_MARKETPLACE: PolicyRow[] = [
  { area: 'Privacy', policy: 'GDPR + DPDP compliance', status: 'compliant' },
  { area: 'Privacy', policy: 'Consent management + data subject rights', status: 'active' },
  { area: 'Marketplace', policy: 'Amazon SP-API OAuth validation', status: 'enforced' },
  { area: 'Marketplace', policy: 'Webhook signature verification', status: 'enforced' },
  { area: 'Marketplace', policy: 'Credential rotation every 90 days', status: 'policy' },
];

export const DEMO_AI_SECURITY: ControlRow[] = [
  { domain: 'AI Security', control: 'Prompt injection defense', status: 'active' },
  { domain: 'AI Security', control: 'Agent tenant isolation', status: 'enforced' },
  { domain: 'Application', control: 'OWASP Top 10 compliance', status: 'validated' },
  { domain: 'Application', control: 'SAST + DAST in CI pipeline', status: 'passing' },
  { domain: 'Application', control: 'Dependency + secrets scanning', status: 'passing' },
];

export const DEMO_INFRA_SECOPS: InfraSecOpsRow[] = [
  { area: 'Infrastructure', detail: 'Container hardening + WAF', status: 'enforced' },
  { area: 'Infrastructure', detail: 'DDoS protection active', status: 'active' },
  { area: 'SecOps', detail: 'SIEM + continuous monitoring', status: 'active' },
  { area: 'SecOps', detail: 'Threat detection + correlation', status: 'active' },
];

export const DEMO_INCIDENT_VULN: IncidentVulnRow[] = [
  { category: 'Incident', item: 'Open incidents', status: 'contained', count: 1 },
  { category: 'Incident', item: 'Mean time to contain (min)', status: 'within_sla', count: 42 },
  { category: 'Vulnerability', item: 'Critical CVEs', status: 'patched', count: 0 },
  { category: 'Vulnerability', item: 'High CVEs pending', status: 'in_progress', count: 3 },
  { category: 'Vulnerability', item: 'Patch compliance %', status: '96%', count: 96 },
];

export const DEMO_COMPLIANCE: ComplianceDashboardRow[] = [
  { metric: 'Security Score', value: 'A', status: 'healthy', score: 94 },
  { metric: 'Compliance Score', value: '92%', status: 'on_track', score: 92 },
  { metric: 'SOC 2 Readiness', value: '85%', status: 'in_progress', score: 85 },
  { metric: 'Critical Risks', value: '2 open', status: 'managed', score: 88 },
  { metric: 'Audit Readiness', value: 'Q3 scheduled', status: 'preparing', score: 80 },
  { metric: 'Access Reviews', value: '100% complete', status: 'compliant', score: 100 },
];

export function buildSecurityDashboard() {
  return {
    council_roles: COUNCIL_ROLES.length,
    zero_trust_pillars: ZERO_TRUST_PILLARS.length,
    compliance_standards: COMPLIANCE_STANDARDS.length,
    security_metrics: SECURITY_METRICS.length,
    incident_phases: INCIDENT_RESPONSE.length,
  };
}
